import { PointCloudOctree } from '@datalabs-jp/three-loader'
import { EDITOR_DECIMAL_BASE } from '@editor/config/constants'
import { AnchorPoints, PointArray } from '@editor/interfaces/interfaces'
import { Box3, Line3, Mesh, Object3D, PerspectiveCamera, Points, Vector3 } from 'three'
import { ArcballControls, TransformControlsGizmo, TransformControls as TransformControlsImpl } from 'three-stdlib'
import { ArcballControls as ThreeArcballControls } from 'three/addons/controls/ArcballControls.js'

import { findNormalTowardsB } from './Shape'
import { roundNumber } from './Util'

/**
 * Get the distance and the center point between two points
 * @param {AnchorPoints} anchor
 * @returns center point and distance
 */
export const calculateCenterAndDistance = (anchor: AnchorPoints): [PointArray, number] | undefined => {
  if (anchor.points.length !== 2) {
    return undefined
  }
  const line = new Line3(new Vector3(...anchor.points[0]), new Vector3(...anchor.points[1]))
  const center = line.getCenter(new Vector3())
  return [center.toArray(), roundNumber(line.distance(), EDITOR_DECIMAL_BASE)]
}

/**
 * Get the center position of a mesh. This will return localized
 * to the provided mesh, not world position.
 *
 * @param mesh ThreeJS mesh
 * @returns Vector3 position
 */
export const getCenterPoint = (mesh: Mesh): Vector3 => {
  const { geometry } = mesh
  const center = new Vector3()
  geometry.computeBoundingBox()

  if (!geometry.boundingBox) {
    return center
  }

  geometry.boundingBox.getCenter(center)
  return center
}

/**
 * Calculate the center between two points.
 *
 * @param pointA
 * @param pointB
 */
export const getCenterOfTwoPoints = (pointA: Vector3, pointB: Vector3): Vector3 => {
  const center = new Vector3()
  center.addVectors(pointA, pointB).multiplyScalar(0.5)
  return center
}

/**
 * Within Transform control, except for gizmos (which actually meshes) with X, Y or Z name,
 * hide and disable other gizmos/meshes that used for translation, rotation and scaling
 * @param mesh ThreeJS Mesh or Object3D
 */
/* istanbul ignore next */
const removeGizmo = (mesh: Mesh | Object3D) => {
  if (['XYZX', 'XYZY', 'XYZZ', 'XYZ', 'XY', 'YZ', 'XZ', 'E'].includes(mesh.name)) {
    mesh.visible = false
    mesh.layers.disableAll()
  }
}

/**
 * Remove unused gizmos from TransformControls
 * @param transformControl TransformControls
 */
/* istanbul ignore next */
export const removeUnsedGizmos = (transformControl: TransformControlsImpl) => {
  /* eslint-disable dot-notation */
  /* eslint-disable @typescript-eslint/no-unsafe-member-access */
  /* eslint-disable @typescript-eslint/no-unsafe-call */
  const gizmo = transformControl['gizmo'] as TransformControlsGizmo

  gizmo['gizmo'].scale.children.forEach((mesh: Mesh) => {
    removeGizmo(mesh)
  })
  gizmo['gizmo'].translate.children.forEach((mesh: Mesh) => {
    removeGizmo(mesh)
  })
  gizmo['gizmo'].rotate.children.forEach((mesh: Mesh) => {
    removeGizmo(mesh)
  })
  gizmo['picker'].scale.children.forEach((mesh: Object3D) => {
    removeGizmo(mesh)
  })
  gizmo['picker'].translate.children.forEach((mesh: Object3D) => {
    removeGizmo(mesh)
  })
  gizmo['picker'].rotate.children.forEach((mesh: Object3D) => {
    removeGizmo(mesh)
  })
  /* eslint-enable @typescript-eslint/no-unsafe-call */
  /* eslint-enable @typescript-eslint/no-unsafe-member-access */
  /* eslint-enable dot-notation */
}

/**
 * Computes the radius of the bounding sphere that encloses a given bounding box.
 * @param bbox - The bounding box (Box3) to compute the sphere radius from.
 * @returns The radius of the bounding sphere.
 */
export function computeBoundingSphereRadius(bbox: Box3): number {
  // Compute the diagonal length of the bounding box
  const diagonal = bbox.max.clone().sub(bbox.min).length()

  // The radius is half of the diagonal length
  return diagonal * 0.5
}

/**
 * Focus the camera on a specific target point at a given distance.
 *
 * @param arcballControls ArcballControls instance
 * @param camera Camera instance
 * @param target The target point to focus on (Vector3)
 * @param radius The maximum distance from the target point to the camera.
 *               If the camera is too far, it will be moved closer.
 */
export const focusCamera = (
  controls: ArcballControls | ThreeArcballControls,
  camera: PerspectiveCamera,
  target: Vector3,
  maxDistance: number = 2,
) => {
  // Save the current position and up vector of the camera
  const currentCameraPosition = camera.position.clone()
  const up = camera.up.clone()

  // vector from the camera to the anchor point
  const cameraToAnchor = new Vector3(...target).sub(currentCameraPosition.clone())

  // angle between the camera's forward vector and the vector from the camera to the anchor point
  const angle = cameraToAnchor.angleTo(new Vector3(0, 0, -1).applyQuaternion(camera.quaternion.clone()))

  // center position of viewing plane calculated from the camera's position and the distance to the anchor point
  const distance = Math.min(cameraToAnchor.length(), maxDistance)
  const centerOfTheClickedViewingPlane = currentCameraPosition
    .clone()
    .sub(new Vector3(0, 0, distance * Math.cos(angle)).applyQuaternion(camera.quaternion.clone()))

  // Set the target of the camera to the clicked point
  const newCameraPosition = currentCameraPosition
    .clone()
    .add(new Vector3(...target))
    .sub(centerOfTheClickedViewingPlane)

  if (controls instanceof ThreeArcballControls) {
    // Disable zoom temporarily to prevent zooming in when focusing
    const zoomEnabled = controls.enableZoom
    controls.enableZoom = false
    controls.focus(target, 1)
    controls.enableZoom = zoomEnabled
  } else {
    controls.setTarget(target.x, target.y, target.z)
    controls.reset()

    // Set the camera position and up vector back to the original
    camera.position.set(newCameraPosition.x, newCameraPosition.y, newCameraPosition.z)
    camera.up.set(up.x, up.y, up.z)
  }
}

export const focusCameraToPlane = (
  controls: ArcballControls | ThreeArcballControls,
  camera: PerspectiveCamera,
  point: PointArray,
  normal: Vector3,
  right?: Vector3,
  up?: Vector3,
  distance?: number,
) => {
  // Save the current position and up vector of the camera
  const point3 = new Vector3(...point)
  const currentCameraPosition = camera.position.clone()

  // Get forward vector of the camera
  const cameraForwardVector = findNormalTowardsB(currentCameraPosition, point3, normal)

  // Calculate the new camera position based on the current distance
  const dist = distance || currentCameraPosition.distanceTo(point3)
  const newCameraPosition = point3.clone().add(cameraForwardVector.clone().negate().multiplyScalar(dist))

  // Update camera orientation. ThreeJS's Arcball and three-stdlib's ArcballControls handle this differently.
  if (controls instanceof ThreeArcballControls) {
    camera.position.copy(newCameraPosition)
    camera.lookAt(point3)
    camera.updateMatrixWorld()

    if (controls.target instanceof Vector3) {
      controls.target.copy(point3)
    }
    controls.update()
  } else {
    controls.setTarget(...point)
    controls.reset()

    camera.position.copy(newCameraPosition)

    const cameraUpVector = up || new Vector3().crossVectors(right || new Vector3(), normal).normalize()
    camera.up.set(cameraUpVector.x, cameraUpVector.y, cameraUpVector.z)
  }
}

/**
 * Computes the center and radius of a point cloud.
 *
 * @param pointCloud The point cloud to compute the center and radius for.
 * @returns An object containing the center (Vector3) and radius (number) of the point cloud.
 */
export const getCenterAndRadius = (
  pointClouds: Points | PointCloudOctree | (Points | PointCloudOctree)[],
): {
  bbox: Box3
  center: Vector3
  radius: number
} => {
  const pcds = Array.isArray(pointClouds) ? pointClouds : [pointClouds]
  const bbox = pcds.reduce<Box3>((acc, pcd) => {
    if (pcd instanceof Points) {
      pcd.geometry.computeBoundingBox()
      if (pcd.geometry.boundingBox) acc.union(pcd.geometry.boundingBox)
    } else if (pcd instanceof PointCloudOctree) {
      acc.union(pcd.getBoundingBoxWorld())
    }
    return acc
  }, new Box3())

  const center = bbox.getCenter(new Vector3())
  const radius = computeBoundingSphereRadius(bbox)

  return { bbox, center, radius }
}
